// ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../../components/js-components/button';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate password
    if (!validatePassword(formData.password)) {
      setError('Password must be at least 8 characters long, include an uppercase letter and a number.');
      setLoading(false);
      return;
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        token,
        newPassword: formData.password
      });

      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-container flex justify-center items-center min-h-screen bg-gray-100">
      <div className="reset-password-card w-full max-w-md bg-white shadow-lg rounded-xl p-8">
        {error && (
          <div 
            className="error-banner flex items-center bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r-lg shadow-md" 
            role="alert"
          >
            <AlertTriangle className="mr-4 text-red-500" size={24} />
            <span className="font-semibold text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div 
            className="success-banner flex items-center bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded-r-lg shadow-md" 
            role="alert"
          >
            <CheckCircle className="mr-4 text-green-500" size={24} />
            <span className="font-semibold text-sm">{success}</span>
          </div>
        )}

        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">Reset Your Password</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label 
              htmlFor="password" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              New Password
            </label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your new password"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Password must be at least 8 characters, include uppercase, lowercase, and a number.
            </p>
          </div>

          <div className="mb-6">
            <label 
              htmlFor="confirmPassword" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your new password"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <Button 
            type="submit" 
            disabled={loading || !token} 
            className="w-full bg-red-500 text-white py-2 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
          >
            {loading ? "Resetting Password..." : "Reset Password"}
          </Button>
        </form>

        <p className="text-center mt-4 text-sm text-gray-600">
          Remember your password? {" "}
          <a 
            href="/login" 
            className="text-blue-500 hover:text-blue-700 font-semibold"
          >
            Log in
          </a>
        </p>
      </div>
    </div>
  );
}

export default ResetPassword;